import UserController from "../services/system/user/user-controller";

/**
 * SCR-070 — Nguồn quyền của CMS: đọc từ server (`GET /user/my-permission`), KHÔNG đọc
 * claim `authorities` trong JWT.
 *
 * Vì sao: token sống 7 ngày và hệ thống không có refresh token, nên quyền nằm trong token là
 * ảnh chụp lúc đăng nhập. Admin thêm/xoá quyền ở màn "Phân quyền nhóm người dùng" thì menu và
 * nút trên CMS vẫn y nguyên tới cả tuần, trong khi server (từ SCR-070) đã gác theo DB
 * — lệch nhau sinh ra đúng cảnh "menu hiện mà bấm vào 403" và ngược lại.
 *
 * Endpoint đọc thẳng `role_permission` nên luôn tươi; ở đây chỉ giữ một bản chụp để:
 *  - `checkPermission()` là hàm ĐỒNG BỘ, gọi ngay trong render của ~253 chỗ — không await được;
 *  - mở lại tab không phải chờ mạng mới thấy menu.
 */

const STORAGE_KEY = 'sas_school_per';
const USERNAME_KEY = 'username';

/** Làm mới định kỳ: quyền đổi là chậm nhất 5 phút CMS tự thấy, không cần F5. */
const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

/** Quay lại tab sau khoảng này thì làm mới luôn (đừng bắn request mỗi lần chuyển tab). */
const REFRESH_ON_FOCUS_AFTER_MS = 60 * 1000;

let codes = null;          // Set<string> | null (null = chưa có bản chụp nào)
let loadedAt = 0;
let inFlight = null;       // Promise của lần gọi đang chạy - chống gọi trùng
let autoRefreshStarted = false;
const listeners = new Set();

function currentUsername() {
    try {
        return localStorage.getItem(USERNAME_KEY) || '';
    } catch (e) {
        return '';
    }
}

/** Nạp bản chụp từ localStorage (gọi 1 lần khi module khởi tạo). */
function readSnapshot() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || !Array.isArray(parsed.codes)) return null;
        // Bản chụp của người khác (đổi tài khoản trên cùng máy) thì bỏ, đừng dùng quyền của họ.
        if (parsed.user && parsed.user !== currentUsername()) return null;
        return { codes: new Set(parsed.codes), at: parsed.at || 0 };
    } catch (e) {
        return null;
    }
}

function writeSnapshot(set) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
            codes: Array.from(set),
            at: Date.now(),
            user: currentUsername()
        }));
    } catch (e) {
        // localStorage đầy / bị chặn: vẫn chạy được bằng bản trong RAM.
    }
}

const snapshot = readSnapshot();
if (snapshot) {
    codes = snapshot.codes;
    loadedAt = snapshot.at;
}

function sameSet(a, b) {
    if (!a || !b || a.size !== b.size) return false;
    for (const value of a) {
        if (!b.has(value)) return false;
    }
    return true;
}

function notify() {
    listeners.forEach(fn => {
        try {
            fn();
        } catch (e) {
            console.error('permissionStore listener error:', e);
        }
    });
}

/** Đã có bản quyền để render chưa (bản chụp cũ cũng tính). */
export function isPermissionLoaded() {
    return codes !== null;
}

export function getPermissionCodes() {
    return codes;
}

export function hasPermissionCode(code) {
    return codes !== null && codes.has(code);
}

/**
 * Gọi `/user/my-permission` và cập nhật store.
 *
 * ⚠️ Gọi thất bại (mạng rớt, server 500) thì GIỮ NGUYÊN bản cũ — coi là "không có quyền nào"
 * sẽ xoá sạch menu của người đang làm việc, trông như mất hết chức năng.
 *
 * @returns {Promise<boolean>} true nếu lấy được danh sách mới
 */
export function loadPermissions() {
    if (inFlight) return inFlight;
    inFlight = new Promise(resolve => {
        UserController.getPermission(res => {
            if (res && res.success && Array.isArray(res.data)) {
                const next = new Set(res.data.filter(Boolean).map(c => String(c).trim()).filter(Boolean));
                const changed = !sameSet(next, codes);
                codes = next;
                loadedAt = Date.now();
                writeSnapshot(next);
                if (changed) notify();
                resolve(true);
                return;
            }
            console.warn('Không lấy được danh sách quyền, dùng tạm bản đã lưu.', res && res.errorCode);
            resolve(false);
        });
    }).finally(() => {
        inFlight = null;
    });
    return inFlight;
}

/**
 * Bảo đảm có quyền trước khi render lần đầu.
 * Có bản chụp rồi thì render ngay và làm mới ở phía sau (không để người dùng nhìn spinner).
 */
export function ensurePermissionsLoaded() {
    if (codes !== null) {
        loadPermissions();
        return Promise.resolve(true);
    }
    return loadPermissions();
}

/** Làm mới theo chu kỳ + khi quay lại tab. Gọi một lần cho cả app. */
export function startPermissionAutoRefresh() {
    if (autoRefreshStarted) return;
    autoRefreshStarted = true;

    setInterval(() => {
        if (document.visibilityState === 'visible') loadPermissions();
    }, REFRESH_INTERVAL_MS);

    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState !== 'visible') return;
        if (Date.now() - loadedAt < REFRESH_ON_FOCUS_AFTER_MS) return;
        loadPermissions();
    });
}

/** Đăng ký nhận thông báo khi danh sách quyền ĐỔI (để render lại menu/nút). */
export function subscribePermissions(listener) {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

/** Dùng khi đăng xuất (clearSession cũng xoá key này trong localStorage). */
export function clearPermissionCache() {
    codes = null;
    loadedAt = 0;
    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
        // không sao
    }
}
